import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Reusable "Field changes" History section — the native replacement for
// NetSuite's System Information subtab (task #53). Drop it onto any native
// record page:
//
//   <RecordHistory table="inventory_items" rowId={item.id} />
//
// Reads GET /api/history/:table/:rowId (allow-listed on the server; only
// history-enabled authored tables answer). Events are grouped per write:
// who, when, via what, and each field's old → new value. Sync/cron churn is
// hidden by default with a toggle to reveal it.
//
// Styling: scoped .nit-* classes from NativeItems.css (per the NativeItems
// patterns) plus the .nit-hist-* additions at the bottom of that file.

const fmtStamp = (d) => (d ? new Date(d).toLocaleString() : 'unknown');

const OPERATION_LABEL = { insert: 'created', update: 'edited', delete: 'deleted' };

function Value({ v }) {
  if (v == null || v === '') return <span className="nit-muted">(empty)</span>;
  return <span className="nit-hist-value">{String(v)}</span>;
}

export default function RecordHistory({ table, rowId, title = 'Field changes' }) {
  const [includeSystem, setIncludeSystem] = useState(false);
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!table || !rowId) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    axios
      .get(`/api/history/${encodeURIComponent(table)}/${encodeURIComponent(rowId)}`, {
        params: includeSystem ? { includeSystem: 'true' } : {},
      })
      .then(({ data }) => { if (!cancelled) setState({ data, loading: false, error: null }); })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.response?.data?.error || err.message });
      });
    return () => { cancelled = true; };
  }, [table, rowId, includeSystem]);

  const { data, loading, error } = state;
  const events = (data && data.events) || [];

  return (
    <div className="nit-card">
      <div className="nit-hist-head">
        <h3>{title}</h3>
        <label className="nit-hist-toggle">
          <input
            type="checkbox"
            checked={includeSystem}
            onChange={(e) => setIncludeSystem(e.target.checked)}
          />
          show sync/cron changes
        </label>
      </div>

      {loading && <div className="nit-loading">Loading field changes…</div>}
      {error && <div className="nit-error">{error}</div>}

      {!loading && !error && events.length === 0 && (
        <div className="nit-empty">
          No field changes recorded for this record yet.
          <br />
          {data?.note || 'Native history begins when change capture is attached; earlier changes remain in NetSuite (read-only).'}
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <>
          {events.map((ev, i) => (
            <div className="nit-hist-event" key={`${ev.txnId}-${ev.operation}-${i}`}>
              <div className="nit-hist-meta">
                <strong>{ev.actorEmail}</strong>
                <span className="nit-tag">{ev.actorSource}</span>
                <span className="nit-muted">{OPERATION_LABEL[ev.operation] || ev.operation}</span>
                <span className="nit-muted">{fmtStamp(ev.changedAt)}</span>
              </div>
              {ev.operation === 'update' && ev.changes.length > 0 && (
                <div className="nit-table-wrap">
                  <table className="nit-table nit-hist-table">
                    <thead>
                      <tr><th>Field</th><th>Old value</th><th /><th>New value</th></tr>
                    </thead>
                    <tbody>
                      {ev.changes.map((c, j) => (
                        <tr key={j}>
                          <td><code>{c.field}</code></td>
                          <td className="nit-hist-old"><Value v={c.oldValue} /></td>
                          <td className="nit-hist-arrow">→</td>
                          <td className="nit-hist-new"><Value v={c.newValue} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {ev.operation === 'insert' && (
                <p className="nit-caption">Record created.</p>
              )}
              {ev.operation === 'delete' && (
                <p className="nit-caption">Record deleted{ev.deletedSnapshot ? ' (snapshot retained in the change log)' : ''}.</p>
              )}
            </div>
          ))}
          <p className="nit-caption">
            {events.length} change event{events.length === 1 ? '' : 's'}, newest first
            {data?.truncated ? ' (older events truncated)' : ''}.
            {' '}{data?.note}
          </p>
        </>
      )}
    </div>
  );
}
